import { AfterViewChecked, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { getFirebaseBackend } from 'src/app/authUtils';
import { EventService } from 'src/app/core/services/event.service';
import { VeraService } from 'src/app/services/vera/vera.service';
import { VeraConversation, VeraConversationService } from 'src/app/services/vera/vera-conversation.service';
import { VeraMessage, extractOptions, normalizeOptions, optionLabel, renderMessageText } from 'src/app/shared/vera-chatbot/vera-message.utils';

@Component({
	selector: 'app-vera-ai-chat',
	templateUrl: './vera-ai-chat.component.html',
	styleUrls: ['./vera-ai-chat.component.scss'],
})
export class VeraAiChatComponent implements OnInit, AfterViewChecked {
	@ViewChild('messagesEnd') messagesEnd: ElementRef<HTMLDivElement>;

	loading = false;
	loadingConversations = false;
	draft = '';
	messages: VeraMessage[] = [];
	conversations: VeraConversation[] = [];
	activeConversationId: number | null = null;
	renamingConversationId: number | null = null;
	renameDraft = '';

	readonly suggestionChips = ['What is the status of my leads?', 'Show me my recent activity', 'Help me with a campaign'];

	private userName = '';
	private userId: number | null = null;
	private shouldScroll = false;

	constructor(
		private veraService: VeraService,
		private veraConversationService: VeraConversationService,
		private route: ActivatedRoute,
		private eventService: EventService
	) {}

	ngOnInit(): void {
		const authUser = getFirebaseBackend()?.getAuthenticatedUser();
		this.userName = authUser?.FirstName || '';
		const parsedId = Number(authUser?.IdUser);
		this.userId = Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;

		const requestedConversation = Number(this.route.snapshot.queryParamMap.get('conversation'));
		this.loadConversations(Number.isFinite(requestedConversation) && requestedConversation > 0 ? requestedConversation : null);
	}

	ngAfterViewChecked(): void {
		if (this.shouldScroll) {
			this.scrollToBottom();
			this.shouldScroll = false;
		}
	}

	loadConversations(preselectId: number | null = null): void {
		if (!this.userId) {
			return;
		}
		this.loadingConversations = true;
		this.veraConversationService.listConversations(this.userId).subscribe({
			next: (conversations) => {
				this.loadingConversations = false;
				this.conversations = conversations ?? [];
				const target = preselectId && this.conversations.some((c) => c.id === preselectId) ? preselectId : this.conversations[0]?.id;
				if (target) {
					this.selectConversation(target);
				} else {
					this.startNewConversation();
				}
			},
			error: (err) => {
				console.error('Vera: error al listar conversaciones', err);
				this.loadingConversations = false;
			},
		});
	}

	selectConversation(conversationId: number): void {
		this.activeConversationId = conversationId;
		this.messages = [];
		this.eventService.broadcast('vera:conversation-changed', { conversationId });
		this.veraConversationService.getMessages(conversationId).subscribe({
			next: (records) => {
				this.messages = (records ?? []).map((record) => ({
					role: record.role,
					text: record.message,
					time: this.formatTime(record.created_at),
					options: record.options ?? undefined,
					optionsResolved: record.role === 'assistant' && record.options?.length ? true : undefined,
				}));
				this.shouldScroll = true;
			},
			error: (err) => console.error('Vera: error al cargar mensajes', err),
		});
	}

	startNewConversation(): void {
		this.activeConversationId = null;
		this.messages = [];
		this.pushAssistant(this.greeting());
		this.eventService.broadcast('vera:conversation-changed', { conversationId: null });
	}

	startRename(conversation: VeraConversation, event: MouseEvent): void {
		event.stopPropagation();
		this.renamingConversationId = conversation.id;
		this.renameDraft = this.conversationTitle(conversation);
	}

	cancelRename(): void {
		this.renamingConversationId = null;
	}

	confirmRename(conversation: VeraConversation): void {
		const title = this.renameDraft.trim();
		this.renamingConversationId = null;

		if (!title || title === conversation.title || !this.userId) {
			return;
		}

		this.veraConversationService.renameConversation(conversation.id, this.userId, title).subscribe({
			next: () => {
				conversation.title = title;
			},
			error: (err) => console.error('Vera: error al renombrar conversación', err),
		});
	}

	deleteConversation(conversation: VeraConversation, event: MouseEvent): void {
		event.stopPropagation();
		if (!this.userId) {
			return;
		}
		this.veraConversationService.deleteConversation(conversation.id, this.userId).subscribe({
			next: () => {
				this.conversations = this.conversations.filter((c) => c.id !== conversation.id);
				if (this.activeConversationId === conversation.id) {
					const next = this.conversations[0]?.id;
					next ? this.selectConversation(next) : this.startNewConversation();
				}
			},
		});
	}

	sendChip(text: string): void {
		this.draft = text;
		this.send();
	}

	onInputKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.send();
		}
	}

	send(): void {
		const text = this.draft.trim();
		if (!text || this.loading) {
			return;
		}

		this.pushUser(text);
		this.draft = '';
		this.loading = true;

		this.ensureConversationId(text).subscribe({
			next: (conversationId) => {
				this.persistMessage(conversationId, 'user', text);
				this.veraService.sendMessage(text, String(conversationId)).subscribe({
					next: (res) => this.handleResponse(res, conversationId),
					error: () => this.handleError(),
				});
			},
			error: () => this.handleError(),
		});
	}

	selectOption(message: VeraMessage, option: Record<string, any>): void {
		if (this.loading || message.optionsResolved) {
			return;
		}

		message.optionsResolved = true;

		const label = optionLabel(option);
		this.pushUser(label);
		this.loading = true;

		this.ensureConversationId(label).subscribe({
			next: (conversationId) => {
				this.persistMessage(conversationId, 'user', label);
				this.veraService.sendMessage(label, String(conversationId), option).subscribe({
					next: (res) => this.handleResponse(res, conversationId),
					error: () => this.handleError(),
				});
			},
			error: () => this.handleError(),
		});
	}

	get activeConversation(): VeraConversation | undefined {
		return this.conversations.find((c) => c.id === this.activeConversationId);
	}

	render(text: string): string {
		return renderMessageText(text);
	}

	optionLabel(option: Record<string, any>): string {
		return optionLabel(option);
	}

	trackByIndex(index: number): number {
		return index;
	}

	conversationTitle(conversation: VeraConversation): string {
		return conversation.title?.trim() || 'New conversation';
	}

	private greeting(): string {
		return `Hi${this.userName ? ' ' + this.userName : ''}! I'm Vera, your LMS assistant. How can I help you today?`;
	}

	private handleResponse(res: { reply?: string; message?: string; output?: string; options?: Record<string, any> | Record<string, any>[] } | null | undefined, conversationId: number): void {
		this.loading = false;
		const rawText = res?.reply || res?.message || res?.output || 'I did not get a response from the assistant.';
		const { text, options } = extractOptions(rawText);
		const finalOptions = options ?? normalizeOptions(res?.options);
		this.pushAssistant(text, finalOptions);
		this.persistMessage(conversationId, 'assistant', text, finalOptions);
	}

	private handleError(): void {
		this.loading = false;
		this.pushAssistant('I could not connect to the assistant right now. Please try again in a few seconds.');
	}

	private ensureConversationId(firstMessage: string): Observable<number> {
		if (this.activeConversationId) {
			return of(this.activeConversationId);
		}

		if (!this.userId) {
			return throwError(() => new Error('No authenticated user'));
		}

		const title = firstMessage.length > 40 ? `${firstMessage.slice(0, 40)}…` : firstMessage;
		return this.veraConversationService.createConversation(this.userId, title).pipe(
			map((res) => {
				this.activeConversationId = res.id;
				this.conversations = [{ id: res.id, user_id: this.userId as number, title, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, ...this.conversations];
				this.eventService.broadcast('vera:conversation-changed', { conversationId: res.id });
				return res.id;
			})
		);
	}

	private persistMessage(conversationId: number, role: 'user' | 'assistant', text: string, options?: Record<string, any>[]): void {
		if (!this.userId) {
			return;
		}
		this.veraConversationService.saveMessage(conversationId, this.userId, role, text, options).subscribe({ error: (err) => console.error('Vera: error al guardar mensaje', err) });
	}

	private pushUser(text: string): void {
		this.messages.push({ role: 'user', text, time: this.now() });
		this.shouldScroll = true;
	}

	private pushAssistant(text: string, options?: Record<string, any>[]): void {
		this.messages.push({ role: 'assistant', text, time: this.now(), options: options?.length ? options : undefined });
		this.shouldScroll = true;
	}

	private now(): string {
		return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	private formatTime(iso: string): string {
		const date = new Date(iso);
		return isNaN(date.getTime()) ? this.now() : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	private scrollToBottom(): void {
		try {
			this.messagesEnd?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
		} catch {
			// ignore
		}
	}
}
